import React, { useCallback, useEffect, useRef, useState } from "react";

import { Box, Paper, Typography } from "@mui/material";
import { AlertBanner, Button, InputField } from "@dishopsaas/design-system";
import { PRIMARY, TRANSPARENT } from "../../../constants/configuration";
import { getVerificationStyles } from "./VerificationCode.styles";
import { verificationCodeService } from "./VerificationCode.services";

interface VerificationCodeProps {
  email: string;
  secondaryColor: string;
  dark: boolean;
  onVerify?: (code: string) => Promise<void>;
  errorMessage?: string;
  onResend?: () => Promise<void> | void;
  onClearError?: () => void;
  loading: boolean;
  setTime;
  time;
}

export default function VerificationCode({
  email,
  secondaryColor,
  dark,
  onVerify,
  onResend,
  onClearError,
  errorMessage,
  loading,
  setTime,
  time,
}: VerificationCodeProps) {
  const [code, setCode] = useState<string[]>(
    verificationCodeService.createCode(),
  );

  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  const styles = getVerificationStyles({
    dark,
    secondaryColor,
  });

  const expired = time === 0;

  const completed = verificationCodeService.isCompleted(code);

  useEffect(() => {
    if (expired) return;

    const interval = setInterval(() => {
      setTime((t) => Math.max(t - 1, 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [expired]);

  const formattedTime = verificationCodeService.formatTime(time);

  const changeCode = useCallback(
    (index: number, value: string) => {
      if (!verificationCodeService.isValidDigit(value)) {
        return;
      }

      setCode((prev) => {
        const next = [...prev];

        next[index] = value;

        return next;
      });

      if (value && index < code.length - 1) {
        inputs.current[index + 1]?.focus();
      }
    },
    [code.length],
  );

  const keyDown = useCallback(
    (index: number, event: React.KeyboardEvent) => {
      if (event.key === "Backspace" && !code[index] && index > 0) {
        inputs.current[index - 1]?.focus();
      }
    },
    [code],
  );

  const submit = async () => {
    if (!completed || expired || loading) {
      return;
    }

    await onVerify?.(code.join(""));
  };

  const resend = async () => {
    onClearError?.();
    setCode(verificationCodeService.createCode());

    setTime(verificationCodeService.resetTimer());

    inputs.current[0]?.focus();

    await onResend?.();
  };

  const pasteCode = (event: React.ClipboardEvent) => {
    const digits = verificationCodeService.getPastedCode(
      event.clipboardData.getData("text"),
    );

    if (!digits) return;

    setCode(digits);

    setTimeout(() => {
      inputs.current[5]?.focus();
    }, 0);
  };

  return (
    <Box sx={styles.container}>
      <Paper sx={styles.card}>
        <Box sx={styles.icon}>
          <Typography variant="h2">🔐</Typography>
        </Box>

        <Typography
          variant="h4"
          fontWeight={900}
          color={styles.colors.text}
          sx={{
            fontSize: {
              xs: "1.8rem",
              sm: "2.125rem",
            },
          }}
        >
          Authentification requise
        </Typography>

        <Typography sx={styles.subtitle}>
          Nous avons envoyé un code à 6 chiffres à l'adresse mail
        </Typography>

        <Box sx={styles.email}>
          <Typography fontWeight={700} color={styles.colors.text}>
            {email}
          </Typography>
        </Box>

        <Box sx={styles.codeContainer}>
          {code.map((digit, index) => (
            <InputField
              key={index}
              inputRef={(el) => (inputs.current[index] = el)}
              value={digit}
              onChange={(e) => changeCode(index, e.target.value)}
              onKeyDown={(e) => keyDown(index, e)}
              onPaste={pasteCode}
              slotProps={{
                htmlInput: {
                  maxLength: 1,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                },
              }}
              sx={styles.input}
            />
          ))}
        </Box>

        {errorMessage && (
          <Box sx={styles.error}>
            <AlertBanner
              severity="error"
              noShadow
              message={<Typography variant="body1">{errorMessage}</Typography>}
            />
          </Box>
        )}

        <Box sx={styles.timer}>⏱ Code expire dans {formattedTime}</Box>

        <Typography sx={styles.subtitle}>
          Vous n'avez pas reçu le code ?
        </Typography>

        <Button
          colorType={TRANSPARENT}
          disabled={!expired}
          onClick={resend}
          sx={styles.button}
          label={expired ? "Renvoyer" : "Attendez"}
        />

        <Button
          disabled={!completed || expired || loading}
          loading={loading}
          onClick={submit}
          colorType={PRIMARY}
          sx={styles.buttonConfirm}
          label="Continuer"
        />
      </Paper>
    </Box>
  );
}
